import React, { useState, useEffect } from 'react';
import { AddProjectOffcanvas } from './add';
import { DeleteProjectModal } from './delete';
import { EditProjectOffcanvas } from './edit';
import { ProjectsList, ProjectFilters as ProjectsListFilters } from './list';

import api from '../../libs/axios';
import { useDebounce } from '../../hooks/useDebounce';
import { useAuth } from '../../provider/auth.provider';

interface Activity {
    id: number;
    activity_name: string;
    activity_short_name: string;
}

interface Client {
    id: number;
    client_full_name: string;
    client_short_name: string;
}

interface StateCircle {
    id: number;
    state_name: string;
}

interface ClientProject {
    id: number;
    project_name: string;
}

interface User {
    id: number;
    first_name: string;
    last_name: string;
    email: string;
}

interface ProjectSite {
    id: number;
    site_name: string;
    status: string;
    assigned_engineer?: User;
}

interface UserProject {
    id: number;
    user_id: number;
    project_id: number;
    user?: User;
}

interface Project {
    id: number;
    activity_id: number;
    client_id: number;
    state_circle_id: number;
    client_project_id?: number;
    project_name: string;
    project_short_name: string;
    project_description?: string;
    budget?: number;
    nearby_site_radius_km?: number | null;
    inc_checkin_without_verification?: boolean;
    status: string;
    created_by: number;
    created_at: string;
    updated_at: string;
    activity?: Activity;
    client?: Client;
    state_circle?: StateCircle;
    client_project?: ClientProject;
    user_projects?: UserProject[];
    sites?: ProjectSite[];
}

interface Pagination {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
}

interface ProjectSort {
    sort_by: string;
    sort_order: 'ASC' | 'DESC';
}

interface ProjectFormData {
    activity_id: number;
    client_id: number;
    state_circle_id: number;
    client_project_id?: number;
    project_name: string;
    project_short_name: string;
    project_description?: string;
    user_ids?: number[];
    budget?: number;
    nearby_site_radius_km?: number | null;
    inc_checkin_without_verification?: boolean;
}

interface EditProjectFormData extends ProjectFormData {
    id: number;
    status?: string;
    project_manager_id?: number;
}

type ProjectFilters = ProjectsListFilters;

interface ProjectManagementProps {
    onRequirementsClick?: (project: Project) => void;
    hideAddProjectButton?: boolean;
    filterByCurrentUser?: boolean; // New prop to enable user assignment filtering
}

export const ProjectManagement: React.FC<ProjectManagementProps> = ({ 
    onRequirementsClick, 
    hideAddProjectButton,
    filterByCurrentUser = false // Default to false for backward compatibility
}) => {
    const { user } = useAuth();

    // State management
    const [projects, setProjects] = useState<Project[]>([]);
    const [pagination, setPagination] = useState<Pagination>({
        total: 0,
        page: 1,
        limit: 10,
        totalPages: 0
    });

    // Loading states
    const [projectsLoading, setProjectsLoading] = useState(false);
    const [actionLoading, setActionLoading] = useState(false);

    // Modal/Offcanvas states
    const [showAddProject, setShowAddProject] = useState(false);
    const [showEditProject, setShowEditProject] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [selectedProject, setSelectedProject] = useState<Project | null>(null);

    // Search and filter states
    const [searchTerm, setSearchTerm] = useState('');
    const [advancedFilters, setAdvancedFilters] = useState<ProjectFilters>({});
    const [currentSort, setCurrentSort] = useState<ProjectSort>({
        sort_by: 'created_at',
        sort_order: 'DESC'
    });

    // Debounced search — API fires only after 400 ms of inactivity
    const debouncedSearch = useDebounce(searchTerm, 400);

    // Reference data states
    const [activities, setActivities] = useState<Activity[]>([]);
    const [clients, setClients] = useState<Client[]>([]);
    const [stateCircles, setStateCircles] = useState<StateCircle[]>([]);
    const [users, setUsers] = useState<User[]>([]);
    const [clientProjects, setClientProjects] = useState<ClientProject[]>([]);

    // API Functions
    const numericFilterKeys = new Set(['client_id', 'activity_id', 'state_circle_id', 'budget']);

    const fetchProjects = async (
        search: string = '',
        page: number = 1,
        limit?: number,
        filters: ProjectFilters = advancedFilters,
        sort: ProjectSort = currentSort
    ) => {
        setProjectsLoading(true);
        try {
            const queryParams: any = {
                page: page,
                limit: limit ?? pagination.limit
            };

            if (search.trim()) {
                queryParams.search = search;
            }

            // Apply user filter if enabled and user is not admin
            if (filterByCurrentUser && user && user.role.level !== 1) {
                queryParams.assigned_user_ids = user.id;
            }

            if (filters) {
                Object.entries(filters).forEach(([key, value]) => {
                    if (value === undefined || value === '' || (Array.isArray(value) && value.length === 0)) {
                        return;
                    }

                    if (Array.isArray(value)) {
                        queryParams[key] = value.join(',');
                        return;
                    }

                    if (typeof value === 'string') {
                        const trimmed = value.trim();
                        if (!trimmed) {
                            return;
                        }

                        if (numericFilterKeys.has(key)) {
                            const parsedNumber = Number(trimmed);
                            if (!Number.isNaN(parsedNumber)) {
                                queryParams[key] = parsedNumber;
                            }
                            return;
                        }

                        queryParams[key] = trimmed;
                        return;
                    }

                    queryParams[key] = value;
                });
            }

            if (sort?.sort_by) {
                queryParams.sort_by = sort.sort_by;
                queryParams.sort_order = sort.sort_order;
            }

            const response = await api.get('/projects/list', {
                params: queryParams
            });

            if (response.data.success) {
                setProjects(response.data.data.projects || []);
                setPagination(response.data.data.pagination || pagination);
            } else {
                console.error('API returned success: false');
            }
        } catch (error) {
            console.error('Error fetching projects:', error);
        } finally {
            setProjectsLoading(false);
        }
    };

    const fetchProjectById = async (projectId: number): Promise<Project | null> => {
        try {
            const response = await api.get(`/projects/${projectId}/details`);

            if (response.data.success) {
                return response.data.data.project;
            }
            return null;
        } catch (error) {
            console.error('Error fetching project by ID:', error);
            return null;
        }
    };

    const fetchReferenceData = async () => {
        try {
            const [activitiesRes, clientsRes, stateCirclesRes, usersRes] = await Promise.all([
                api.get('/activities/active'),
                api.get('/clients/list'),
                api.get('/state-circle/list'),
                api.get('/projects/team/list')
            ]);

            if (activitiesRes.data.success) {
                setActivities(activitiesRes.data.data.activities || []);
            }

            if (clientsRes.data.success) {
                setClients(clientsRes.data.data.clients || []);
            }

            if (stateCirclesRes.data.success) {
                setStateCircles(stateCirclesRes.data.data.states || []);
            }

            if (usersRes.data.success) {
                setUsers(usersRes.data.data || []);
            }
        } catch (error) {
            console.error('Error fetching reference data:', error);
        }
    };

    const fetchClientProjects = async (clientId: number) => {
        try {
            const response = await api.get(`/clients/${clientId}/projects`);
            if (response.data.success) {
                setClientProjects(response.data.data.client_projects || []);
            }
        } catch (error) {
            console.error('Error fetching client projects:', error);
        }
    };

    const createProject = async (projectData: ProjectFormData) => {
        setActionLoading(true);
        try {
            const response = await api.post('/projects/create', projectData);

            if (response.data.success) {
                await fetchProjects();
                setShowAddProject(false);
                console.log('Project created successfully');
            } else {
                console.error('Failed to create project:', response.data.message);
            }
        } catch (error) {
            console.error('Error creating project:', error);
        } finally {
            setActionLoading(false);
        }
    };

    const updateProject = async (projectData: EditProjectFormData) => {
        setActionLoading(true);
        try {
            const response = await api.put(`/projects/${projectData.id}/update`, projectData);

            if (response.data.success) {
                await fetchProjects();
                setShowEditProject(false);
                setSelectedProject(null);
                console.log('Project updated successfully');
            } else {
                console.error('Failed to update project:', response.data.message);
            }
        } catch (error) {
            console.error('Error updating project:', error);
        } finally {
            setActionLoading(false);
        }
    };

    const deleteProject = async () => {
        if (!selectedProject) return;

        setActionLoading(true);
        try {
            const response = await api.delete(`/projects/${selectedProject.id}`);

            if (response.data.success) {
                await fetchProjects();
                setShowDeleteModal(false);
                setSelectedProject(null);
                console.log('Project deleted successfully');
            } else {
                console.error('Failed to delete project:', response.data.message);
            }
        } catch (error) {
            console.error('Error deleting project:', error);
        } finally {
            setActionLoading(false);
        }
    };

    // Event handlers
    const handleSearch = (search: string) => {
        setSearchTerm(search);
        // Actual API call is triggered by the debouncedSearch effect below
    };

    // Fire fetch when debounced search value settles
    useEffect(() => {
        fetchProjects(debouncedSearch, 1, undefined, advancedFilters);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debouncedSearch]);

    const handlePageChange = (page: number) => {
        // Keep current search and filters when changing page
        fetchProjects(searchTerm, page, undefined, advancedFilters);
    };

    const handleLimitChange = (limit: number) => {
        setPagination(prev => ({
            ...prev,
            limit,
            page: 1
        }));
        // When page size changes, go back to page 1 with current filters
        fetchProjects(searchTerm, 1, limit, advancedFilters);
    };

    const handleAdvancedFilter = (filters: ProjectFilters) => {
        setAdvancedFilters(filters);
        // Apply new filters from page 1 and hit backend
        fetchProjects(searchTerm, 1, undefined, filters);
    };

    const handleSort = (sortBy: string, sortOrder: 'ASC' | 'DESC') => {
        const sort = { sort_by: sortBy, sort_order: sortOrder };
        setCurrentSort(sort);
        fetchProjects(searchTerm, pagination.page, undefined, advancedFilters, sort);
    };

    const handleAddProject = () => {
        setShowAddProject(true);
    };

    const handleEditProject = async (project: Project) => {
        // Fetch full project details if needed
        const fullProject = await fetchProjectById(project.id);
        if (fullProject) {
            setSelectedProject(fullProject);
            setShowEditProject(true);
        }
    };

    const handleDeleteProject = (project: Project) => {
        setSelectedProject(project);
        setShowDeleteModal(true);
    };

    const handleCloseModals = () => {
        setShowAddProject(false);
        setShowEditProject(false);
        setShowDeleteModal(false);
        setSelectedProject(null);
    };

    // Initialize data on component mount
    useEffect(() => {
        fetchProjects();
        fetchReferenceData();
    }, []);

    return (
        <div className="">
            <div className="">
                {/* Main Projects List */}
                <ProjectsList
                    projects={projects}
                    pagination={pagination as any}
                    onSearch={handleSearch}
                    onSort={handleSort}
                    onPageChange={handlePageChange}
                    onLimitChange={handleLimitChange}
                    onAddProject={handleAddProject}
                    hideAddProjectButton={hideAddProjectButton}
                    onEditProject={handleEditProject}
                    onDeleteProject={handleDeleteProject}
                    clients={clients}
                    activities={activities}
                    stateCircles={stateCircles}
                    users={users}
                    onAdvancedFilter={handleAdvancedFilter}
                    advancedFilters={advancedFilters}
                    loading={projectsLoading}
                    actionLoading={actionLoading}
                    onRequirementsClick={onRequirementsClick}
                    onBulkUpdated={() => fetchProjects(searchTerm, pagination.page, undefined, advancedFilters)}
                />

                {/* Add Project Offcanvas */}
                <AddProjectOffcanvas
                    isOpen={showAddProject}
                    onClose={handleCloseModals}
                    onSubmit={createProject}
                    activities={activities}
                    clients={clients}
                    stateCircles={stateCircles}
                    users={users as User[]}
                    clientProjects={clientProjects}
                    onClientChange={fetchClientProjects}
                    loading={actionLoading}
                />

                {/* Edit Project Offcanvas */}
                <EditProjectOffcanvas
                    isOpen={showEditProject}
                    onClose={handleCloseModals}
                    onSubmit={updateProject}
                    project={selectedProject}
                    activities={activities}
                    clients={clients}
                    stateCircles={stateCircles}
                    users={users as User[]}
                    clientProjects={clientProjects}
                    onClientChange={fetchClientProjects}
                    loading={actionLoading}
                />

                {/* Delete Project Modal */}
                <DeleteProjectModal
                    isOpen={showDeleteModal}
                    onClose={handleCloseModals}
                    onDelete={deleteProject}
                    text={
                        selectedProject
                            ? `Are you sure you want to delete the project "${selectedProject.project_name}"? This action cannot be undone and will remove all associated data including sites and billing information.`
                            : ''
                    }
                    loading={actionLoading}
                />
            </div>
        </div>
    );
};